import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import API from '../../../../services/API'
import { TenantSettingsContext } from '../../../../App'
import { formatMoney } from '../../../../helpers/currency'
import { getWorkerPaymentType } from '../../../../helpers/workerPaymentType'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { DataGrid } from '@mui/x-data-grid'
import EmployeeSelector from '../../../events/EmployeeSelector'
import DocumentsField from '../../../../helpers/DocumentsField'
import { fileRecord, uploadDocument } from '../../../../helpers/uploadDocument'
import useDocumentPreview from '../../../../components/Documents/useDocumentPreview'
import {
  InvoiceRowDialog,
  buildInvoiceRows,
  invoiceGridSx,
  invoiceRowClassName,
  invoiceRowColumns,
  invoiceTotalsWithVat,
  isInvoiceRowSelectable,
  normaliseInvoiceSelection,
  selectionLabel,
  selectionTotal,
  splitInvoiceSelection,
} from '../../../../helpers/staffInvoiceRows'

const employeeName = (invoice) =>
  `${invoice?.employee?.userAccount?.firstName ?? ''} ${
    invoice?.employee?.userAccount?.lastName ?? ''
  }`.trim() || 'Unknown employee'

const isInvoiceWorker = (employee) =>
  getWorkerPaymentType(employee?.payroll?.employmentType ?? '') === 'Invoice'

const statusOptions = [
  'All',
  'Submitted',
  'Approved',
  'Queried',
  'Matched',
  'Rejected',
]

const StaffInvoices = ({ refreshKey }) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [invoices, setInvoices] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [statusFilter, setStatusFilter] = useState('All')
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  })
  const [listError, setListError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [queryNote, setQueryNote] = useState('')
  const [reviewError, setReviewError] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [eligibleShifts, setEligibleShifts] = useState([])
  // VAT info for the selected worker; a registered worker invoices net + VAT
  const [eligibleVat, setEligibleVat] = useState(null)
  const [shiftSelection, setShiftSelection] = useState([])
  const [invoiceForm, setInvoiceForm] = useState({
    invoiceNumber: '',
    invoiceDate: moment().format('YYYY-MM-DD'),
    amount: '',
    notes: '',
  })
  const [invoiceFiles, setInvoiceFiles] = useState([])
  const [invoiceError, setInvoiceError] = useState(null)
  const [viewedInvoiceRow, setViewedInvoiceRow] = useState(null)
  const { openPreview, previewModal } = useDocumentPreview()

  const currency = (value) => formatMoney(value, orgSettings?.currency)

  const loadInvoices = () => {
    setLoading(true)
    API.get(
      `/payroll/staff-invoices?status=${statusFilter}&page=${paginationModel.page}&pageSize=${paginationModel.pageSize}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setInvoices(res.data.invoices ?? [])
          setTotalCount(res.data.totalCount ?? 0)
          setListError(null)
        } else {
          setListError(res.data?.error ?? 'Unable to load staff invoices.')
        }
      })
      .catch(() => setListError('Unable to load staff invoices.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadInvoices()
  }, [refreshKey, statusFilter, paginationModel])

  const closeReview = () => {
    setSelectedInvoice(null)
    setQueryNote('')
    setReviewError(null)
  }

  const updateStatus = (status) => {
    setReviewError(null)
    API.post(`/payroll/staff-invoices/${selectedInvoice._id}/status`, {
      status,
      queryNote,
    }).then((res) => {
      if (res.status === 200) {
        closeReview()
        loadInvoices()
      } else {
        setReviewError(
          res.data?.error || 'Unable to update this invoice. Please try again.',
        )
      }
    })
  }

  // Who decided and when, for the outcome banner
  const reviewedLabel = (invoice) => {
    const name = [invoice?.reviewedBy?.firstName, invoice?.reviewedBy?.lastName]
      .filter(Boolean)
      .join(' ')
    const when = invoice?.reviewedAt
      ? moment(invoice.reviewedAt).format('D MMM YYYY, HH:mm')
      : null
    return `${name ? ` by ${name}` : ''}${when ? ` on ${when}` : ''}`
  }

  const reviewStatus = selectedInvoice?.status
  // Approved, Rejected and Matched have been decided; Submitted and Queried
  // are still open for review
  const reviewDecided = ['Approved', 'Rejected', 'Matched'].includes(
    reviewStatus,
  )
  const reviewOutcome =
    reviewStatus === 'Matched'
      ? {
          severity: 'success',
          text: `Approved${reviewedLabel(selectedInvoice)}${
            selectedInvoice?.payRun?.reference
              ? ` and matched to pay run ${selectedInvoice.payRun.reference}`
              : ' and matched to a paid pay run'
          }.`,
        }
      : reviewStatus === 'Approved'
        ? {
            severity: 'success',
            text: `Approved${reviewedLabel(selectedInvoice)}.`,
          }
        : reviewStatus === 'Rejected'
          ? {
              severity: 'error',
              text: `Rejected${reviewedLabel(selectedInvoice)}.`,
            }
          : null

  const loadEligibleShifts = (employee) => {
    setEligibleShifts([])
    setEligibleVat(null)
    setShiftSelection([])
    if (!employee?._id) return

    API.get('/payroll/staff-invoices/eligible-shifts', {
      params: { employee: employee._id },
    })
      .then((res) => {
        if (res.status === 200) {
          // Older servers return a bare array with no VAT info
          if (Array.isArray(res.data)) {
            setEligibleShifts(res.data)
          } else {
            setEligibleShifts(res.data?.lines ?? [])
            setEligibleVat(res.data?.vat ?? null)
          }
        } else {
          setInvoiceError(
            res.data?.error ??
              "Unable to load this worker's eligible shifts.",
          )
        }
      })
      .catch(() =>
        setInvoiceError("Unable to load this worker's eligible shifts."),
      )
  }

  const closeUpload = () => {
    setUploadOpen(false)
    setInvoiceError(null)
  }

  const uploadStaffInvoice = async () => {
    const uploadAmount = Number(invoiceForm.amount)
    const hasUploadAmount =
      invoiceForm.amount !== '' && Number.isFinite(uploadAmount)

    if (
      !selectedEmployee?._id ||
      !shiftSelection.length ||
      !hasUploadAmount ||
      invoiceAmountMismatch
    ) {
      return
    }

    setUploading(true)
    setInvoiceError(null)

    try {
      const file = invoiceFiles[0]
      const key = file
        ? await uploadDocument(file, 'payroll/staff-invoices').catch(() => null)
        : null
      if (file && !key) {
        throw new Error(
          'Unable to upload the invoice file. Check your connection and try again.',
        )
      }

      const created = await API.post('/payroll/staff-invoices', {
        employee: selectedEmployee._id,
        ...splitInvoiceSelection(shiftSelection),
        invoiceNumber: invoiceForm.invoiceNumber,
        invoiceDate: invoiceForm.invoiceDate,
        amount: invoiceForm.amount,
        notes: invoiceForm.notes,
        ...(key ? { file: fileRecord(file, key) } : {}),
      })

      // 4xx payloads carry a readable `error`; 5xx is handled globally
      if (created.status !== 200) {
        throw new Error(
          created.data?.error ||
            'Unable to submit this invoice. Please try again.',
        )
      }

      setUploadOpen(false)
      setSelectedEmployee(null)
      setEligibleShifts([])
      setShiftSelection([])
      setInvoiceFiles([])
      setInvoiceForm({
        invoiceNumber: '',
        invoiceDate: moment().format('YYYY-MM-DD'),
        amount: '',
        notes: '',
      })
      loadInvoices()
    } catch (err) {
      console.error('Unable to submit staff invoice', err)
      setInvoiceError(err.message)
    } finally {
      setUploading(false)
    }
  }

  // One row per shift plus one per overtime claim; anything already on a live
  // invoice is left out by the server
  const invoiceRows = useMemo(
    () => buildInvoiceRows(eligibleShifts),
    [eligibleShifts],
  )
  const vatRate = eligibleVat?.registered ? eligibleVat.rate : 0
  // Row amounts are net; a VAT-registered worker's invoice total is gross
  const selectedShiftTotals = invoiceTotalsWithVat(
    selectionTotal(invoiceRows, shiftSelection),
    vatRate,
  )
  const invoiceAmount = Number(invoiceForm.amount)
  const hasInvoiceAmount =
    invoiceForm.amount !== '' && Number.isFinite(invoiceAmount)
  // The invoice must match the selected total exactly (to the penny)
  const invoiceAmountMismatch =
    hasInvoiceAmount &&
    Math.abs(invoiceAmount - selectedShiftTotals.gross) > 0.005

  const changeShiftSelection = (selection) => {
    const next = normaliseInvoiceSelection(invoiceRows, Array.from(selection))
    setShiftSelection(next)
    // Keep the amount in step with the selection; it can still be edited
    setInvoiceForm((prev) => ({
      ...prev,
      amount: next.length
        ? invoiceTotalsWithVat(
            selectionTotal(invoiceRows, next),
            vatRate,
          ).gross.toFixed(2)
        : '',
    }))
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack gap={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          gap={1}
        >
          <Stack>
            <Typography variant="title2">Staff Invoices</Typography>
            <Typography variant="body2" color="text.secondary">
              Review invoice worker submissions against completed unpaid shifts.
            </Typography>
          </Stack>
          <Stack
            direction="row"
            gap={1}
            alignSelf={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
              sx={{ minWidth: 140 }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Button
              startIcon={<UploadFileIcon />}
              variant="outlined"
              onClick={() => setUploadOpen(true)}
            >
              Upload Invoice
            </Button>
          </Stack>
        </Stack>
        {loading && <LinearProgress />}
        {listError && (
          <Alert severity="error" onClose={() => setListError(null)}>
            {listError}
          </Alert>
        )}
        {!loading && totalCount === 0 && statusFilter === 'All' ? (
          <Alert severity="info" variant="outlined" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" fontWeight={600}>
              No staff invoices to review.
            </Typography>
            <Typography variant="body2">
              Invoice workers can submit an invoice for completed unpaid shifts, or
              payroll can upload one received outside AmbuNet. Each invoice is
              checked against the selected shifts and expected pay before it is
              approved, queried, rejected, or matched to a pay run.
            </Typography>
          </Alert>
        ) : (
          <Box sx={{ height: 320 }}>
            <DataGrid
              rows={invoices}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              pagination
              paginationMode="server"
              rowCount={totalCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              onRowClick={({ row }) => {
                setSelectedInvoice(row)
                setQueryNote(row.queryNote ?? '')
              }}
              columns={[
                {
                  field: 'employee',
                  headerName: 'Employee',
                  minWidth: 180,
                  flex: 1,
                  valueGetter: (value, row) => employeeName(row),
                },
                {
                  field: 'invoiceNumber',
                  headerName: 'Invoice',
                  minWidth: 140,
                  valueGetter: (value) => value || 'Not supplied',
                },
                {
                  field: 'invoiceDate',
                  headerName: 'Date',
                  minWidth: 130,
                  valueGetter: (value) =>
                    value ? moment(value).format('DD MMM YYYY') : '',
                },
                {
                  field: 'amount',
                  headerName: 'Amount',
                  minWidth: 120,
                  valueGetter: (value) => currency(value),
                },
                {
                  field: 'expectedAmount',
                  headerName: 'Expected',
                  minWidth: 120,
                  valueGetter: (value) => currency(value),
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  minWidth: 120,
                  renderCell: ({ row }) => (
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === 'Approved' || row.status === 'Matched'
                          ? 'success'
                          : row.status === 'Rejected'
                            ? 'error'
                            : 'warning'
                      }
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  ),
                },
                {
                  field: 'shifts',
                  headerName: 'Shifts',
                  minWidth: 110,
                  valueGetter: (value, row) =>
                    `${value?.length ?? 0}${
                      row.overtimeShifts?.length
                        ? ` + ${row.overtimeShifts.length} OT`
                        : ''
                    }`,
                },
                {
                  field: 'generatedByPlatform',
                  headerName: 'Source',
                  minWidth: 110,
                  renderCell: ({ row }) => (
                    <Chip
                      size="small"
                      label={row.generatedByPlatform ? 'Generated' : 'Uploaded'}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  ),
                },
              ]}
            />
          </Box>
        )}
      </Stack>

      <Dialog
        open={!!selectedInvoice}
        onClose={closeReview}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{selectedInvoice?.invoiceNumber || 'Staff Invoice'}</DialogTitle>
        <DialogContent dividers>
          {selectedInvoice && (
            <Stack gap={2}>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={employeeName(selectedInvoice)} />
                <Chip label={selectedInvoice.status} />
                <Chip label={`${selectedInvoice.shifts?.length ?? 0} shifts`} />
                {selectedInvoice.overtimeShifts?.length > 0 && (
                  <Chip
                    label={`${selectedInvoice.overtimeShifts.length} overtime`}
                  />
                )}
                <Chip label={currency(selectedInvoice.amount)} color="warning" />
                <Chip
                  label={`Expected ${currency(selectedInvoice.expectedAmount)}`}
                />
                {/* Only invoices priced for a VAT-registered worker carry a
                    snapshot; older invoices show no VAT chips at all */}
                {selectedInvoice.vatAmount > 0 && (
                  <>
                    <Chip
                      variant="outlined"
                      label={`Net ${currency(selectedInvoice.netAmount)}`}
                    />
                    <Chip
                      variant="outlined"
                      label={`VAT ${currency(selectedInvoice.vatAmount)}${
                        selectedInvoice.vatNumber
                          ? ` · ${selectedInvoice.vatNumber}`
                          : ''
                      }`}
                    />
                  </>
                )}
              </Stack>
              {reviewOutcome && (
                <Alert severity={reviewOutcome.severity} variant="outlined">
                  {reviewOutcome.text}
                  {selectedInvoice.queryNote
                    ? ` Note: ${selectedInvoice.queryNote}`
                    : ''}
                </Alert>
              )}
              {reviewStatus === 'Queried' && (
                <Alert severity="warning" variant="outlined">
                  Queried{reviewedLabel(selectedInvoice)}. Approve or reject it
                  once the worker has responded.
                </Alert>
              )}
              {!reviewDecided && (
                <TextField
                  label="Review note"
                  value={queryNote}
                  onChange={(e) => setQueryNote(e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                />
              )}
              {reviewError && (
                <Alert severity="error" onClose={() => setReviewError(null)}>
                  {reviewError}
                </Alert>
              )}
              {selectedInvoice.file?.key ? (
                <Stack gap={0.5} alignItems="flex-start">
                  <Button
                    startIcon={<VisibilityIcon />}
                    variant="outlined"
                    onClick={() => openPreview(selectedInvoice.file)}
                  >
                    View Invoice File
                  </Button>
                  {selectedInvoice.file.name && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedInvoice.file.name}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Alert severity="info" variant="outlined">
                  No invoice file was attached.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReview}>Close</Button>
          {(reviewStatus === 'Approved' || reviewStatus === 'Rejected') && (
            <Button
              color="inherit"
              variant="outlined"
              onClick={() => updateStatus('Submitted')}
            >
              Undo decision
            </Button>
          )}
          {!reviewDecided && (
            <>
              <Button color="error" onClick={() => updateStatus('Rejected')}>
                Reject
              </Button>
              <Button color="warning" onClick={() => updateStatus('Queried')}>
                Query
              </Button>
              <Button variant="contained" onClick={() => updateStatus('Approved')}>
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={uploadOpen} onClose={closeUpload} fullWidth maxWidth="md">
        <DialogTitle>Upload Staff Invoice</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            {invoiceError && (
              <Alert severity="error" onClose={() => setInvoiceError(null)}>
                {invoiceError}
              </Alert>
            )}
            <EmployeeSelector
              valueType="employee"
              value={selectedEmployee}
              onChange={(e, employee) => {
                setSelectedEmployee(employee)
                loadEligibleShifts(employee)
              }}
              label="Invoice Worker"
              additionalSelectors="payroll"
              fullWidth
            />
            {selectedEmployee && !isInvoiceWorker(selectedEmployee) && (
              <Alert severity="warning" variant="outlined">
                This employee is not configured as an invoice worker.
              </Alert>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
              <TextField
                label="Invoice Number"
                value={invoiceForm.invoiceNumber}
                onChange={(e) =>
                  setInvoiceForm((prev) => ({
                    ...prev,
                    invoiceNumber: e.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Invoice Date"
                type="date"
                value={invoiceForm.invoiceDate}
                onChange={(e) =>
                  setInvoiceForm((prev) => ({
                    ...prev,
                    invoiceDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Invoice Amount"
                type="number"
                value={invoiceForm.amount}
                onChange={(e) =>
                  setInvoiceForm((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                inputProps={{ min: 0, step: 0.01 }}
                helperText={
                  vatRate
                    ? `Must match the selected total including VAT: ${currency(selectedShiftTotals.net)} net + ${currency(selectedShiftTotals.vat)} VAT = ${currency(selectedShiftTotals.gross)}`
                    : 'Must match the selected total'
                }
                fullWidth
                required
              />
            </Stack>
            <DocumentsField
              files={invoiceFiles}
              onFilesChange={setInvoiceFiles}
              single
              accept="application/pdf,image/*"
              caption="Optional — the invoice as a PDF or photo"
              acceptLabel="PDF or image files"
              pendingLabel="Uploads when you submit"
              disabled={uploading}
            />
            {invoiceAmountMismatch && (
              <Alert severity="error" variant="outlined">
                Invoice amount {currency(invoiceAmount)} must match the selected
                total {currency(selectedShiftTotals.gross)}, which includes any
                approved overtime ticked below{vatRate ? ' and VAT' : ''}.
              </Alert>
            )}
            <TextField
              label="Notes"
              value={invoiceForm.notes}
              onChange={(e) =>
                setInvoiceForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip label={selectionLabel(shiftSelection)} />
              {vatRate ? (
                <>
                  <Chip
                    variant="outlined"
                    label={`Net ${currency(selectedShiftTotals.net)}`}
                  />
                  <Chip
                    variant="outlined"
                    label={`VAT @ ${Math.round(vatRate * 100)}% ${currency(selectedShiftTotals.vat)}`}
                  />
                  <Chip
                    color="warning"
                    label={`Expected ${currency(selectedShiftTotals.gross)}`}
                  />
                </>
              ) : (
                <Chip
                  color="warning"
                  label={`Expected ${currency(selectedShiftTotals.gross)}`}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Overtime claims are listed on their own rows. Approved overtime
              can be ticked with its shift; a claim still awaiting a decision
              can't be invoiced yet. Click a row to see its details.
            </Typography>
            <Box sx={{ height: 340 }}>
              <DataGrid
                rows={invoiceRows}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={shiftSelection}
                onRowSelectionModelChange={changeShiftSelection}
                isRowSelectable={({ row }) =>
                  isInvoiceRowSelectable(row, shiftSelection)
                }
                getRowClassName={invoiceRowClassName}
                onRowClick={({ row }) => setViewedInvoiceRow(row)}
                sx={invoiceGridSx}
                columns={invoiceRowColumns(currency)}
                slots={{
                  noRowsOverlay: () => (
                    <Stack alignItems="center" justifyContent="center" height="100%">
                      <Alert severity="info">
                        No completed unpaid shifts available for this worker.
                      </Alert>
                    </Stack>
                  ),
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUpload}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={uploadStaffInvoice}
            disabled={
              uploading ||
              !selectedEmployee?._id ||
              !shiftSelection.length ||
              !hasInvoiceAmount ||
              invoiceAmountMismatch ||
              !isInvoiceWorker(selectedEmployee)
            }
          >
            {uploading ? 'Uploading...' : 'Submit Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {previewModal}
      <InvoiceRowDialog
        row={viewedInvoiceRow}
        onClose={() => setViewedInvoiceRow(null)}
        format={currency}
      />
    </Paper>
  )
}

export default StaffInvoices
